// The "Export CSV" action of an office list (Session GAP9, SC10 gap 9). It lives in the list's overflow menu, so there is no new button on the page's
// main row and no new page. Pressing it downloads the list as the screen is showing it: the same filters, search and sort, every row (not just the
// page of 50), through the authenticated route GET /api/exports/<list>.csv. The money columns arrive for an admin only; that is the server's decision.
//
// It says what happened in plain words: how many rows were written, a note when the server cut a very long list at its cap, and an inline
// error (never a silent failure) when the export did not work.
import { useState, type ReactNode } from "react";
import { OverflowMenu, overflowItemCls } from "./OverflowMenu";
import { ApiError, downloadListCsv, type ExportListKey } from "../lib/api";

function friendly(err: unknown): string {
  if (err instanceof ApiError && err.message) return err.message;
  return "The export did not work. Please try again.";
}

// extraItems (Session EXP1): a page that already has a More menu (Call-outs) puts its own items in this one, so there is still ONE menu.
// disabled: the screen is showing nothing (every status unticked), so there is nothing to export.
export function ExportCsvMenu({
  list,
  params,
  label = "More actions",
  extraItems,
  disabled = false,
}: {
  list: ExportListKey;
  params?: Record<string, string | undefined>;
  label?: string;
  extraItems?: ReactNode;
  disabled?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      const out = await downloadListCsv(list, params ?? {});
      const counted = out.truncated
        ? `Only the first ${out.rows.toLocaleString("en-GB")} of ${out.total.toLocaleString("en-GB")} rows were exported. Narrow the list with a filter and export again for the rest.`
        : `Exported ${out.rows.toLocaleString("en-GB")} ${out.rows === 1 ? "row" : "rows"}.`;
      setNote(
        out.changed
          ? `${counted} The list changed while it was being exported, so a row may be missing or repeated. Export again for an exact copy.`
          : counted,
      );
    } catch (err) {
      setError(friendly(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {error && (
        <span role="alert" className="text-xs text-ele-danger">
          {error}
        </span>
      )}
      {/* The menu closes when the item is pressed, so the busy state is said HERE, beside it (fix round 1, review L1). */}
      {busy && (
        <span role="status" className="text-xs text-ele-body">
          Preparing the CSV...
        </span>
      )}
      {note && !error && !busy && (
        <span role="status" className="text-xs text-ele-body">
          {note}
        </span>
      )}
      <OverflowMenu label={label} closeOnSelect>
        {extraItems}
        <button type="button" role="menuitem" disabled={busy || disabled} onClick={() => void run()} className={overflowItemCls}>
          {busy ? "Preparing the CSV..." : "Export CSV"}
        </button>
      </OverflowMenu>
    </div>
  );
}
